import { type FC, Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getProjects, type Projects } from '@/features/projects/api/projects';
import {
  getProjectListFilters,
  hasUnsupportedProjectFilters,
} from '@/features/projects/utils/project-filters';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';

import { type Estates, getEstates } from '../api/estates';
import type { EstateCardProps } from './estate-grid-card';
import type { SearchParams } from '@/utils/parseSearchParamsToSearchQuery';
import { Button } from '@/components/ui/button';

type EstateAdditionalExtendedInfoProps = {
  creator: string;
  createdAt: string;
};

export type EstateLongCardProps = EstateCardProps &
  EstateAdditionalExtendedInfoProps & { thumbnailId: string };

interface EstatesInfiniteScrollProps {
  filters?: SearchParams;
  filterOwn?: boolean;
  sort?: {
    by?: 'name' | 'rooms' | 'area' | 'price' | 'createdAt' | 'updatedAt' | 'creator';
    order?: 'asc' | 'desc';
  };
  type?: 'estate' | 'project';
  cardRenderer: (estate: Estates[number] | Projects[number]) => JSX.Element;
}

interface EstatesState {
  [key: string]: Estates[number] | Projects[number];
}

export const EstatesInfiniteScroll: FC<EstatesInfiniteScrollProps> = ({
  filters,
  filterOwn,
  sort,
  type = 'estate',
  cardRenderer,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [stopLoad, setStopLoad] = useState(false);
  const [estates, setEstates] = useState<EstatesState | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const { lastElementRef, pageNum, resetPageNum } = useInfiniteScroll<HTMLDivElement>({
    loading,
    stopLoad,
    startPage: 1,
  });

  const loadMoreEstates = useCallback(async () => {
    if (pageNum <= 1) return;

    setLoading(true);
    try {
      setError(null);
      const isNotEmptyFilters = !!(filters && Object.keys(filters).length);
      const estateFilters = filters;
      const projectFilters = getProjectListFilters(filters);
      const isNotEmptyProjectFilters = !!Object.keys(projectFilters).length;

      if (type === 'project' && hasUnsupportedProjectFilters(filters)) {
        setStopLoad(true);
        return;
      }

      const estatesResponse =
        type === 'estate'
          ? await getEstates({
              query: {
                page: pageNum,
                ...(filterOwn && { filterOwn }),
                ...(isNotEmptyFilters && { filters: estateFilters }),
                ...(sort?.by &&
                  sort.order && {
                    sort: {
                      by: sort.by,
                      order: sort.order,
                    },
                  }),
              },
            })
          : await getProjects({
              query: {
                page: pageNum,
                ...(isNotEmptyProjectFilters && { filters: projectFilters }),
                ...(sort?.by &&
                  sort.order && {
                    sort: {
                      by: sort.by,
                      order: sort.order,
                    },
                  }),
              },
            });

      if (!estatesResponse.length) {
        setStopLoad(true);
        return;
      }

      const estatesCollection = estatesResponse.reduce<EstatesState>((acc, v) => {
        acc[v.id] = v;
        return acc;
      }, {});

      setEstates((prev) =>
        prev
          ? {
              ...prev,
              ...estatesCollection,
            }
          : estatesCollection
      );
    } catch (err) {
      setError(err as Error);
      setStopLoad(true);
    } finally {
      setLoading(false);
    }
  }, [filters, filterOwn, pageNum, sort, type]);

  useEffect(() => {
    loadMoreEstates();
  }, [loadMoreEstates]);

  useEffect(() => {
    setEstates(null);
    setError(null);
    resetPageNum();
    setStopLoad(false);
  }, [filters, sort, filterOwn, type, resetPageNum]);

  return (
    <Fragment>
      {estates
        ? Object.keys(estates).map((id) => {
            const estate = estates[id];

            return <div key={estate.id}>{estate ? cardRenderer(estate) : null}</div>;
          })
        : null}
      {error && stopLoad ? (
        <Button
          type="button"
          aria-label={t('retry')}
          disabled={loading}
          onClick={() => {
            setStopLoad(false);
            void loadMoreEstates();
          }}
        >
          {t('tryAgain')}
        </Button>
      ) : null}
      {!stopLoad ? <div ref={lastElementRef} /> : null}
    </Fragment>
  );
};
